/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import React, { Component, Fragment } from 'react';
import { Link, withRouter } from 'react-router-dom'
import { connect } from 'react-redux';
import Draggable from 'react-draggable';
import queryString from 'query-string';
import { loadFrameworkByInitiativeId } from '../../actions/framework';
import { loadUniversalTrackerModal } from '../../actions/universalTrackerModal';
import Loader from '../../components/loader';
import Avatar from '../../components/avatar'
import PhaseColumn from './phaseColumn'
import DefaultLogo from '../../images/logo.svg';
import './styles.scss';
import { UtrvStatus } from '../../constants/status';
import { getUTCEndOf, isBefore, getCalendarDate } from '../../utils/date';

class ManagementFramework extends Component {

  clickEvent = (e, utrId, utrvId) => {
    e.preventDefault();
    this.props.loadUTrModal(utrId, 'frameworks', utrvId);
  };

  constructor(props) {
    super(props);
    this.state = {
      width: undefined,
    };
    this.setWidth = this.setWidth.bind(this);
    this.pageContainer = React.createRef();
  }

  componentDidMount() {
    this.props.getFramework(this.props.match.params.initiativeId);
    this.cellWidth = 240;
    window.addEventListener('resize', this.setWidth);
    this.setWidth();
  }

  componentDidUpdate(prevProps, prevState) {
    prevProps !== this.props && this.setWidth();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.setWidth);
  }

  setWidth() {
    if (this.props.frameworkState.loaded && this.pageContainer) {
      this.setState({ width: this.pageContainer.clientWidth });
    }
  }

  hydrateColumnNumbers(phases, utrMap) {
    utrMap.forEach((utr) => {
      for (const utrv of utr.universalTrackerValues) {
        let column = 0;
        utrv.startColumn = 0;
        utrv.endColumn = 0;
        for (const phase of phases) {
          for (const stage of phase.stages) {
            const utrvStartDate = new Date(utrv.startDate);
            const utrvEndDate = new Date(utrv.endDate);
            const stageStartDate = new Date(stage.startDate);
            const stageEndDate = new Date(stage.endDate);
            if (utrvStartDate >= stageStartDate) {
              utrv.startColumn = utrv.endColumn = column;
            }
            if (utrvStartDate >= stageEndDate) {
              utrv.startColumn = utrv.endColumn = utrv.endColumn = column + 1;
            }
            if (utrvEndDate >= stageStartDate) {
              utrv.endColumn = column;
            }
            column++;
          }
        }
      }
    });
  }

  getGroupTotals(group, utrMap, columnCount) {

    const stagesCount = columnCount - 2;

    const columnMap = [];
    for (let columnNumber = 0; columnNumber < stagesCount; columnNumber++) {
      columnMap[columnNumber] = {
        verified: 0,
        total: 0,
      };
    }

    group.utrs.forEach((utrId) => {
      const utr = utrMap.get(utrId);
      for (const utrv of utr.universalTrackerValues) {
        const columnNumber = utrv.endColumn;

        const columnCounts = columnMap[columnNumber];
        if (utrv.status === 'verified' && utrv.value === true) {
          columnCounts.verified++;
        }
        columnCounts.total++;
        columnMap[columnNumber] = columnCounts;
      }
    });

    return columnMap;
  }

  getTotals(groups, utrMap, columnCount) {

    const stagesCount = columnCount - 2;

    const columnMap = [];
    for (let columnNumber = 0; columnNumber < stagesCount; columnNumber++) {
      columnMap[columnNumber] = {
        verified: 0,
        total: 0,
      };
    }

    groups.forEach((group) => {
      const groupTotalMap = this.getGroupTotals(group, utrMap, columnCount);
      for (let columnNumber = 0; columnNumber < stagesCount; columnNumber++) {
        if (!groupTotalMap[columnNumber]) {
          continue;
        }

        columnMap[columnNumber].verified += groupTotalMap[columnNumber].verified
        columnMap[columnNumber].total += groupTotalMap[columnNumber].total
      }
    });

    return columnMap;
  }

  getUTrMap(frameworkUTrs) {
    let utrMap = new Map();
    frameworkUTrs.forEach((utr) => {
      utr.universalTrackerValues = utr.universalTrackerValues.sort((a, b) => {
        if (new Date(a.startDate) > new Date(b.startDate)) {
          return 1;
        }
        if (new Date(a.startDate) < new Date(b.startDate)) {
          return -1;
        }
        if (new Date(a.startDate) === new Date(b.startDate)) {
          if (new Date(a.endDate) > new Date(b.endDate)) {
            return -1;
          }
          if (new Date(a.endDate) < new Date(b.endDate)) {
            return 1;
          }
        }
        return 0;
      });
      utrMap.set(utr._id, utr);
    });
    return utrMap;
  }

  render() {

    let { loadUTrModal, frameworkState, user } = this.props;
    let { width } = this.state;

    if (!frameworkState.loaded) {
      return (<Loader />);
    }

    // Loaded but there is no data
    if (frameworkState.errored) {
      return <div>
        <h1>Management framework</h1>
        <div>There is no framework at this level yet.</div>
      </div>;
    }

    const utrId = queryString.parse(this.props.location.search)['utr'];
    const utrvId = queryString.parse(this.props.location.search)['utrv'];
    if (utrId) {
      loadUTrModal(utrId, 'frameworks', utrvId);
    }

    // Loaded but there is no data
    if (!frameworkState.data || !frameworkState.data.phases || frameworkState.data.phases.length === 0) {
      return <div>
        <h1>Management framework</h1>
        <div>There is no framework at this level yet.</div>
      </div>;
    }

    // Filter out groups that do not have a single utr assigned to it
    let groups = frameworkState.data.groups.filter(g => g.utrs.length > 0);
    let phases = frameworkState.data.phases;
    const stagesCount = phases.reduce((agg, phase) => agg + phase.stages.length, 0);
    const utrMap = this.getUTrMap(frameworkState.data.frameworkUtrs);
    this.hydrateColumnNumbers(phases, utrMap);

    const tableWidth = (stagesCount + 2) * this.cellWidth;
    const columnCount = stagesCount + 2
    const actionCount = this.getUserActions(frameworkState.data.frameworkUtrs, user)

    const change = width - tableWidth;
    const leftBound = change > 0 ? 0 : change;
    return (
      <div>
        <div ref={element => this.pageContainer = element}>
          <h1>Management framework</h1>
          {this.renderActionList(actionCount)}

          <Draggable axis='x' bounds={{ left: leftBound, right: 0 }}>
            <table style={{ width: `${tableWidth}px` }}
                   className={`frameworks-table ${tableWidth > width ? 'draggable-enabled' : ''}`}>
              {this.renderTableHeader(columnCount, phases)}
              <tbody style={{ border: 'none' }}>
              {this.renderTotalRow(this.getTotals(groups, utrMap, columnCount), 'TOTAL SCORE')}
              {this.renderSpacerRow(columnCount)}
              </tbody>
              {groups.map((group, i) => {

                return (
                  <tbody key={'group-' + i}>
                  {this.renderSpacerRow(columnCount)}
                  {this.renderUTrRows(group, utrMap)}
                  {this.renderSpacerRow(columnCount)}
                  {this.renderTotalRow(this.getGroupTotals(group, utrMap, columnCount), 'SCORE')}
                  {this.renderSpacerRow(columnCount)}
                  </tbody>
                );
              })}

              <tbody>
              {this.renderSpacerRow(columnCount)}
              </tbody>

              <tfoot>
              {this.renderTotalRow(this.getTotals(groups, utrMap, columnCount), 'TOTAL SCORE')}
              </tfoot>
            </table>
          </Draggable>
        </div>
      </div>
    )
  }

  getUserActions(frameworkUtrs, user) {
    let actionCount = 0;

    for (const frameworkUtr of frameworkUtrs) {
      for (const utrv of frameworkUtr.universalTrackerValues) {

        const startDate = new Date(utrv.startDate);
        if (isBefore(new Date(), startDate)) {
          continue;
        }

        const utrvLastUpdate = getUTCEndOf('day', new Date())
        if (isBefore(utrv.lastUpdated, utrvLastUpdate)) {
          continue
        }

        const status = utrv.status;
        switch (status) {
          case 'created':
          case 'rejected':
            if (utrv.stakeholders.stakeholder.includes(user._id)) {
              actionCount++;
            }
            break;
          case 'updated':
            if (utrv.stakeholders.verifier.includes(user._id)) {
              actionCount++;
            }
            break;
          case 'verified':
          default:

        }
      }
    }
    return actionCount;
  }

  renderSpacerRow(columnCount) {
    return (
      <tr>
        <td colSpan={columnCount}>&nbsp;</td>
      </tr>
    );
  }

  calculateUtrValueCells(utr) {
    if (!utr) { return []; }

    let currentColumn = 0;
    let combinedRows = [];
    let currentRow = [];

    // Allocated rows when column overlaps
    for (const utrValue of utr.universalTrackerValues) {
      if (utrValue.startColumn <= currentColumn) {
        if (currentRow.length > 0) {
          combinedRows.push(currentRow);
        }
        currentRow = [];
        currentColumn = 0;
      }

      currentColumn = utrValue.endColumn;
      currentRow.push(utrValue);
    }
    if (currentRow.length > 0) {
      combinedRows.push(currentRow);
    }

    return combinedRows;
  }

  renderUTrRows(group, utrMap) {
    let newGroup = true;

    const groupRows = new Map();
    let groupRowCount = 0;
    group.utrs.forEach((utrId) => {
      const utr = utrMap.get(utrId);
      const utrRows = this.calculateUtrValueCells(utr);
      groupRows.set(utr._id, utrRows);
      groupRowCount += utrRows.length;
    });

    return group.utrs.map((utrId) => {
      let utrCount = 0;
      const utr = utrMap.get(utrId);
      const utrRows = groupRows.get(utrId);
      const utrRowCount = utrRows.length;

      return utrRows.map((row, i) => {
        utrCount++;
        if (newGroup) {
          newGroup = false;
          return (
            <Fragment key={'utrRow-' + i}>
              <tr><td rowSpan={groupRowCount} className='text-center'>
                {group.object.logo &&
                  <Avatar width='70%' className={'organization-logo mb-xs'}>
                    <img alt={group.object.name} src={group.object.logo} />
                  </Avatar>
                }
                {!group.object.logo &&
                  <Avatar width='70%' className={'organization-logo mb-xs'}>
                    <img alt={group.object.name} src={DefaultLogo} />
                  </Avatar>
                }

                <h6>{group.object.name}</h6>
              </td>
                {utrCount === 1 ? <td rowSpan={utrRowCount}>{utr.name}</td> : ''}
                {this.renderUTrCells(row, utr)}
              </tr>
            </Fragment>
          );
        } else {
          return (
            <Fragment key={'utrRow-' + i}>
              <tr>
                {utrCount === 1 && <td rowSpan={utrRowCount}>{utr.name}</td>}
                {this.renderUTrCells(row, utr)}
              </tr>
            </Fragment>
          );
        }
      })
    })
  }

  renderValueIcon(utrValue) {
    switch (utrValue.status) {
      case UtrvStatus.Updated:
        return <span className='material-icons in-progress cursorPointer'>redo</span>;
      case UtrvStatus.Rejected:
        return <span className='material-icons text-ThemeDangerMedium cursorPointer'>undo</span>;
      case UtrvStatus.Verified:
        return <span className='material-icons text-ThemeSuccessMedium cursorPointer'>check</span>;
      default:
        return <span className='material-icons cursorPointer'>more_horiz</span>;
    }
  }


  renderUTrCells(row, utr) {
    const { user } = this.props;
    const userId = user._id;

    let startColumn = 0;
    return row.map((utrValue) => {
      const tdSpacer = utrValue.startColumn - startColumn;
      const utrvWidth = utrValue.endColumn - utrValue.startColumn + 1;
      startColumn = utrValue.endColumn + 1;

      let actionRequired = false;
      switch (utrValue.status) {
        case 'created':
        case 'rejected':
          if (utrValue.stakeholders.stakeholder.includes(userId)) {
            actionRequired = true;
          }
          break;
        case 'updated':
          if (utrValue.stakeholders.verifier.includes(userId)) {
            actionRequired = true;
          }
          break;
        default:
          break;
      }

      const cellClassName = 'frameworks-milestone frameworks-cell frameworks-cell-hover' + (actionRequired ? ' action-required' : '');
      return (
        <Fragment key={utrValue._id}>
          {tdSpacer >= 1 && <td colSpan={tdSpacer}>&nbsp;</td>}
          <td colSpan={utrvWidth} title={getCalendarDate(utrValue.startDate) + ' to ' + getCalendarDate(utrValue.endDate)}>
            <div onClick={(e) => this.clickEvent(e, utr._id, utrValue._id)} className={cellClassName}>
              {utrValue.name} {this.renderValueIcon(utrValue)}
            </div>
          </td>
        </Fragment>
      );
    })
  }

  renderTableHeader(colSpan, phases) {
    return <thead>
      {this.renderSpacerRow(colSpan)}
      <tr>
        <th />
        <th />
        {phases.map((phase, i) => <th key={i} colSpan={phase.stages.length}><b>{phase.name}</b></th>)}
      </tr>
      {this.renderSpacerRow(colSpan)}
      <tr>
        <th>
        </th>
        <th>
        </th>
        {phases.map(phase => PhaseColumn(phase))}
      </tr>
      {this.renderSpacerRow(colSpan)}
    </thead>
  }

  renderTotalRow(columnMap, text = 'SCORE') {
    return <tr>
      <td colSpan='2'>&nbsp;</td>
      {columnMap.map((counts, currentColumn) => {
        return (
          <td key={currentColumn}>
            <div className='frameworks-milestone frameworks-score'>
              <div
                className={counts.verified >= counts.total ? 'text-ThemeSuccessMedium' : ''}>
                <b>
                  {text}: <div className='frameworks-score-count'>{counts.verified}/{counts.total}</div>
                </b>
              </div>
            </div>
          </td>
        );
      })
      }
    </tr>
  }

  renderActionList(actionCount) {
    if (actionCount === 0) {
      return '';
    }

    return (
      <div>
        <div className='frameworks-actions headerTop'>
          You currently have <Link to={'/universal-tracker-management/' + this.props.match.params.initiativeId}>
            <u>{actionCount} action{actionCount > 1 ? 's' : ''}</u>
          </Link> to complete.
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    frameworkState: state.framework,
    user: state.currentUser.data,
  }
}

const mapDispatchToProps = dispatch => {
  return {
    getFramework: (id) => dispatch(loadFrameworkByInitiativeId(id)),
    loadUTrModal: (universalTrackerId, type, universalTrackerValueId) => dispatch(loadUniversalTrackerModal({
      universalTrackerId: universalTrackerId,
      universalTrackerType: type,
      actionValueId: universalTrackerValueId
    }))
  }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ManagementFramework));
